var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import * as React from "react";
import * as UnlinkedApplicationForm from "../../../../../components/UnlinkedApplicationForm";
import { AutoEntity, saveAutoEntity, } from "../../../../../features/unlinked-application/unlinkedApplicationSlice";
import { Autocomplete, Button } from "@savvyinsure/react-ui";
import { completedPage, selectedVehicleInformation, } from "../../../../../lib/Avo";
import { useHistory, useParams } from "react-router-dom";
import AppBody from "../../../../../components/AppBody";
import Header from "../../../../../components/Header";
import IncentivizedBanner from "../../../../../components/IncentivizedBanner";
import { PHASE } from "../../../../../routeConfig";
import { QueryLoader } from "../../../../../components/QueryLoader";
import { ToggleButtonGroup } from "../../../../../components/ToggleButton";
import { transformVehicleInfoToSelectOption } from "../../../../../features/unlinked-application/utils";
import useAppDispatch from "../../../../../hooks/useAppDispatch";
import useAppSelector from "../../../../../hooks/useAppSelector";
import { useForm } from "react-hook-form";
import { useGetVehicleYearsQuery } from "../../../../../services/savvy";
import useIsFlagOn from "../../../../../features/feature-flags/hooks/useIsFlagOn";
var HEADER_TITLE = "Let's get started with your vehicle information.";
var TYPE_OF_ENTITY = AutoEntity.Vehicle;
var ANTIQUE_YEARS = {
    start: 1890,
    end: 1980,
};
export default function UnlinkedApplicationAutoVehicleEditYear() {
    var yearRef = React.useRef(null);
    var dispatch = useAppDispatch();
    var history = useHistory();
    var yearsQuery = useGetVehicleYearsQuery();
    var vehicleId = useParams().vehicleId;
    var showVinEntry = useIsFlagOn("unlinkedApplication_vehicleVINEntry_frontend");
    var commonAnalyticsProperties = useAppSelector(function (state) { return state.analytics.commonAnalyticsProperties; });
    var vehicleToEdit = useAppSelector(function (state) {
        var vehicles = state.unlinkedApplication.auto.vehicles;
        return vehicles.find(function (_a) {
            var id = _a.id;
            return id === vehicleId;
        });
    });
    var isLoading = yearsQuery.isLoading;
    var _a = useForm({
        defaultValues: {
            year: (vehicleToEdit === null || vehicleToEdit === void 0 ? void 0 : vehicleToEdit.year)
                ? transformVehicleInfoToSelectOption(vehicleToEdit.year)
                : undefined,
        },
    }), handleSubmit = _a.handleSubmit, register = _a.register, watch = _a.watch, setValue = _a.setValue, isValid = _a.formState.isValid;
    var yearValue = watch("year");
    function getSearchYearOptions() {
        var _a, _b, _c;
        var yearOptions = (_c = (_b = (_a = yearsQuery.data) === null || _a === void 0 ? void 0 : _a.filter(function (val) { return val.name.length === 4; })) === null || _b === void 0 ? void 0 : _b.map(transformVehicleInfoToSelectOption)) !== null && _c !== void 0 ? _c : [];
        for (var index = ANTIQUE_YEARS.end; index >= ANTIQUE_YEARS.start; index--) {
            yearOptions.push({
                label: index.toString(),
                value: index.toString(),
            });
        }
        return yearOptions;
    }
    var searchYearOptions = getSearchYearOptions();
    var manualYearOptions = searchYearOptions === null || searchYearOptions === void 0 ? void 0 : searchYearOptions.slice(0, 14).concat({
        value: "older",
        label: "Older",
    });
    var onSubmit = handleSubmit(function (data) {
        var _a, _b;
        if (typeof vehicleId !== "string") {
            throw new Error("Error while saving vehicle year: Missing vehicle id");
        }
        var year = (_a = yearsQuery.data) === null || _a === void 0 ? void 0 : _a.find(function (_a) {
            var _b;
            var id = _a.id;
            return id === ((_b = data.year) === null || _b === void 0 ? void 0 : _b.value);
        });
        var isModified = ((_b = vehicleToEdit === null || vehicleToEdit === void 0 ? void 0 : vehicleToEdit.year) === null || _b === void 0 ? void 0 : _b.id) !== (year === null || year === void 0 ? void 0 : year.id);
        var isAntiqueCar = Number(data.year.value) <= ANTIQUE_YEARS.end;
        var nextRoute = isAntiqueCar
            ? PHASE.CALL_TO_SHOP
            : PHASE.UNLINKED_APPLICATION_AUTO_VEHICLES_EDIT_MAKE.replace(":vehicleId", vehicleId);
        completedPage(__assign(__assign({}, commonAnalyticsProperties), { selectedOption: data.year.value, optionSelected: data.year.value }));
        dispatch(saveAutoEntity({
            entity: TYPE_OF_ENTITY,
            data: __assign({ id: vehicleId, year: year }, (isModified && { make: undefined })),
        }));
        history.push({
            pathname: nextRoute,
            search: isAntiqueCar ? "?reason=classic-car" : "",
        });
    });
    var handleYearSelect = React.useCallback(function (year) {
        selectedVehicleInformation(__assign(__assign({}, commonAnalyticsProperties), { vehicleDataType: "year", vehicleDataValue: year.value }));
        setValue("year", year, { shouldValidate: true });
        setTimeout(function () { return onSubmit(); });
    }, [setValue, onSubmit]);
    var handleToggleChange = React.useCallback(function (selected) {
        var _a, _b;
        if (selected === "older") {
            return (_a = yearRef.current) === null || _a === void 0 ? void 0 : _a.focus();
        }
        var year = (_b = yearsQuery.data) === null || _b === void 0 ? void 0 : _b.find(function (_a) {
            var id = _a.id;
            return id === selected;
        });
        if (year) {
            handleYearSelect(transformVehicleInfoToSelectOption(year));
        }
    }, [handleYearSelect, yearsQuery.data]);
    var handleInputYear = function (event) {
        event.currentTarget.value = event.currentTarget.value.replace(/\D/g, "");
    };
    React.useEffect(function () {
        register("year", { required: true });
    }, [register]);
    return (React.createElement(React.Fragment, null,
        React.createElement(Header, { showDropShadow: true },
            React.createElement(UnlinkedApplicationForm.Progress, null)),
        React.createElement(AppBody, { as: "form", action: "#", onSubmit: onSubmit },
            React.createElement(UnlinkedApplicationForm.ScrollingContainer, { className: "pt-6" },
                React.createElement(IncentivizedBanner, { className: "mb-4" }),
                React.createElement(UnlinkedApplicationForm.Header, { className: "mb-4" }, HEADER_TITLE),
                showVinEntry ? (React.createElement("div", { className: "mb-3 w-full" },
                    React.createElement("button", { onClick: function (event) {
                            completedPage(__assign(__assign({}, commonAnalyticsProperties), { selectedOption: event === null || event === void 0 ? void 0 : event.currentTarget.textContent, optionSelected: event === null || event === void 0 ? void 0 : event.currentTarget.textContent }));
                            history.push(PHASE.UNLINKED_APPLICATION_AUTO_VEHICLES_EDIT_VIN.replace(":vehicleId", vehicleId));
                        }, className: "text-sm font-semibold leading-none text-savvy", type: "button" }, "Have your VIN? Get more accurate quotes."))) : null,
                React.createElement(QueryLoader, __assign({}, yearsQuery),
                    React.createElement(Autocomplete, { id: "year", label: "Year", placeholder: "Select Year", value: yearValue, onChange: handleYearSelect, onInput: handleInputYear, options: searchYearOptions, ref: yearRef }),
                    React.createElement("div", { className: "my-4" },
                        React.createElement(ToggleButtonGroup, { variant: "grid", columns: 3, id: "year", options: manualYearOptions, value: yearValue === null || yearValue === void 0 ? void 0 : yearValue.value, onChange: handleToggleChange })))),
            React.createElement("div", { className: "bg-white p-4" },
                React.createElement(Button, { disabled: !isValid || isLoading }, "Continue")))));
}
